import { api } from "@/services/api";

export interface TestCategory {
  id: number;
  name: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface DrugComponent {
  id: number;
  code: string;
  name: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE';
}

export interface VendorTestAlias {
  id: number;
  vendorId: number;
  testTypeId: number;
  aliasName: string;
}

export const masterDataApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Test Categories
    listTestCategories: builder.query<TestCategory[], void>({
      query: () => '/super-admin/test-categories',
      providesTags: ['TestCategories'],
    }),
    createTestCategory: builder.mutation<TestCategory, Partial<TestCategory>>({
      query: (body) => ({
        url: '/super-admin/test-categories',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TestCategories'],
    }),
    updateTestCategory: builder.mutation<TestCategory, { id: number; data: Partial<TestCategory> }>({
      query: ({ id, data }) => {
        // Exclude audit fields and id from update
        const { id: _, ...updateData } = data;
        return {
          url: `/super-admin/test-categories/${id}`,
          method: 'PUT',
          body: updateData,
        };
      },
      invalidatesTags: ['TestCategories'],
    }),
    deleteTestCategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `/super-admin/test-categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TestCategories'],
    }),

    // Drug Components
    listDrugComponents: builder.query<DrugComponent[], void>({
      query: () => '/super-admin/drug-components',
      providesTags: ['DrugComponents'],
    }),
    createDrugComponent: builder.mutation<DrugComponent, Partial<DrugComponent>>({
      query: (body) => ({
        url: '/super-admin/drug-components',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DrugComponents'],
    }),
    updateDrugComponent: builder.mutation<DrugComponent, { id: number; data: Partial<DrugComponent> }>({
      query: ({ id, data }) => {
        // Exclude audit fields and id from update
        const { id: _, ...updateData } = data;
        return {
          url: `/super-admin/drug-components/${id}`,
          method: 'PUT',
          body: updateData,
        };
      },
      invalidatesTags: ['DrugComponents'],
    }),
    deleteDrugComponent: builder.mutation<void, number>({
      query: (id) => ({
        url: `/super-admin/drug-components/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DrugComponents'],
    }),

    // Vendor Aliases
    listVendorAliases: builder.query<VendorTestAlias[], number>({
      query: (vendorId) => `/super-admin/vendors/${vendorId}/aliases`,
      providesTags: ['VendorAliases'],
    }),
    saveVendorAlias: builder.mutation<VendorTestAlias, Partial<VendorTestAlias>>({
      query: (body) => ({
        url: '/super-admin/vendors/aliases',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['VendorAliases'],
    }),
    deleteVendorAlias: builder.mutation<void, number>({
      query: (id) => ({
        url: `/super-admin/vendors/aliases/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['VendorAliases'],
    }),
  }),
});

export const {
  useListTestCategoriesQuery,
  useCreateTestCategoryMutation,
  useUpdateTestCategoryMutation,
  useDeleteTestCategoryMutation,
  useListDrugComponentsQuery,
  useCreateDrugComponentMutation,
  useUpdateDrugComponentMutation,
  useDeleteDrugComponentMutation,
  useListVendorAliasesQuery,
  useSaveVendorAliasMutation,
  useDeleteVendorAliasMutation,
} = masterDataApi;
